import * as React from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { IProps } from '../common/props';
import { useDropzone } from 'react-dropzone';
import { Button } from '../button/Button';
import { useRef, useState } from 'react';
import { FileInputFile } from './FileInputFile';
import {
  CancelUpload,
  defaultMaxFileSize,
  FileInputUploadProgress,
  FileInputValue,
  UloadingInfo,
  UPLOAD_PROGRESS_INFINITE,
} from './props';
import { InputGroup } from '../input-group/InputGroup';
import { useAppConfig } from '@mc/react-app-base';
import { filesize } from 'filesize';
import { showInfo } from '../modal/modalFunctions';

export interface IFileInputCommonProps<T extends FileInputValue> {
  /**
   * Seznam souborů, které chceme akceptovat, např. ['image/jpeg, 'image/png']
   */
  accept?: string | string[];
  placeholder?: string;
  disabled?: boolean;
  /**
   * Pokud true, schová tlačítko na odstranění souboru.
   */
  hideDelete?: boolean;
  /**
   * Načtení href pro stažení souboru pro předanou hodnotu, toto se využije pouze pokud není href definován přímo v hodnotě.
   */
  getHref?: (value: T) => string;
  /**
   * Pokud není uveden href ve value a není definována metoda getHref, tak se při kliknutí na soubor zavolá tato metoda.
   */
  onDownload?: (value: FileInputValue) => void;
  /**
   * Začátek uploadu - formulář by od této chvíle neměl jít submitnout.
   */
  onUploadStart?: () => void;
  /**
   * Konec uploadu - již se nic neuploaduje.
   */
  onUploadStop?: () => void;
  /**
   * Dodatečná akce při smazání souboru.
   */
  onAfterDelete?: () => void;
  /**
   * Upload souboru.
   * @param file soubor pro upload
   * @param onUploadProgress metoda pro možné nastavení upload progresu, pokud nelze určit, není třeba tuto metodu volat
   * @param setCancelUpload pokud api pro cancel pro upload, je možné touto metodou nastavit callback pro cancel (když upload zruší uživatel kliknutí na mazací tlačítko)
   */
  onUpload: (
    file: File,
    onUploadProgress: (progress: FileInputUploadProgress) => void,
    setCancelUpload: (cancel: CancelUpload) => void
  ) => Promise<T>;
}

export interface IFileInputProps<T extends FileInputValue>
  extends IFileInputCommonProps<T>,
    IProps {
  value?: T;
  onChange: (value?: T) => void;
}

export type FileInputProps<T extends FileInputValue> = IFileInputProps<T>;

/**
 * Komponenta pro editaci jednoho souboru s možností změny a smazání.
 */
export const FileInput: <T extends FileInputValue>(
  props: FileInputProps<T>
) => React.ReactElement<FileInputProps<T>> = ({
  className,
  accept,
  placeholder,
  disabled,
  value,
  onUpload,
  onChange,
  onUploadStart,
  onUploadStop,
  onAfterDelete,
  getHref,
  onDownload,
  hideDelete,
}) => {
  const uploadingId = useRef<number>(1);
  const [uloadingInfo, setUloadingInfo] = useState<UloadingInfo | undefined>();
  const [uploadingProgress, setUploadingProgress] = useState<number>(0);
  const cancelUpload = useRef<CancelUpload>(undefined);

  const handleOnDrop = async (files: File[]) => {
    if (files.length === 1) {
      const file = files[0];

      // Necháme soubror, jen pokud není větší než maxFileSize
      const appConfig = useAppConfig();
      const maxFileSize = appConfig.maxFileSize || defaultMaxFileSize;
      if (file.size > maxFileSize) {
        const warningMessage = `Soubor ${file.name} je příliš velký (${filesize(
          file.size,
          {
            locale: 'cs',
          }
        )}).`;

        showInfo({
          intent: 'warning',
          title: 'Soubor nebyl nahrán',
          message: (
            <div>
              {`Maximální povolená velikost souboru je ${filesize(maxFileSize, {
                locale: 'cs',
              })}.`}
              <br />
              <br />
              {warningMessage}
            </div>
          ),
        });

        return;
      }

      const newId = ++uploadingId.current;
      try {
        // Cancel předchozí
        if (cancelUpload.current) {
          cancelUpload.current();
          cancelUpload.current = undefined;
        }
        onUploadStart && onUploadStart();
        setUloadingInfo({
          fileName: file.name,
          type: file.type,
          size: file.size,
        });
        setUploadingProgress(UPLOAD_PROGRESS_INFINITE);

        const newValue = await onUpload(
          file,
          (progress) => {
            if (uploadingId.current === newId) {
              setUploadingProgress(progress.loaded / progress.total);
            }
          },
          (cancel: CancelUpload) => {
            cancelUpload.current = cancel;
          }
        );
        if (uploadingId.current === newId) {
          onChange(newValue);
        }
      } finally {
        if (uploadingId.current === newId) {
          cancelUpload.current = undefined;

          setUploadingProgress(UPLOAD_PROGRESS_INFINITE);
          setUloadingInfo(undefined);
          onUploadStop && onUploadStop();
        }
      }
    }
  };

  const handleDelete = () => {
    uploadingId.current++;

    // Cancel předchozí
    if (cancelUpload.current) {
      cancelUpload.current();
      cancelUpload.current = undefined;
    }

    setUploadingProgress(UPLOAD_PROGRESS_INFINITE);
    setUloadingInfo(undefined);
    onUploadStop && onUploadStop();
    onChange(undefined);
    onAfterDelete?.();
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: accept,
    multiple: false,
    maxFiles: 1,
    preventDropOnDocument: true,
    onDrop: handleOnDrop,
  });

  const rootProps: any = getRootProps({
    className: classNames(className, Classes.FILE_INPUT),
  });
  const { onClick, ...parentRootProps } = rootProps;

  if (value || uloadingInfo) {
    return (
      <FileInputFile
        disabled={disabled}
        value={value}
        onDelete={handleDelete}
        uploadingProgress={uploadingProgress}
        uploadingInfo={uloadingInfo}
        getHref={getHref}
        onDownload={onDownload}
        hideDelete={hideDelete}
      />
    );
  } else {
    let useProps = disabled
      ? { className: classNames(className, Classes.FILE_INPUT) }
      : parentRootProps;
    return (
      <div {...useProps}>
        <input {...getInputProps()} />
        {isDragActive ? (
          <div className={Classes.FILE_INPUT_DRAGGING}>
            Sem přetáhněte soubor
          </div>
        ) : (
          <>
            <InputGroup
              placeholder={placeholder || 'Vyberte nebo přetáhněte soubor'}
              readOnly={!disabled}
              disabled={disabled}
              className={classNames(
                className,
                Classes.INPUT_READONLY_STANDARD,
                Classes.FILE_INPUT_INPUT
              )}
            />
            <Button
              disableStopPropagation
              disabled={disabled}
              className={Classes.FILE_INPUT_ACTION}
              // @ts-ignore
              onClick={onClick}
              dataTest={'pick'}
            >
              Vybrat
            </Button>
          </>
        )}
      </div>
    );
  }
};
